'use client';

import { ArrowDown } from '@/assets/icons/ArrowDown';
import { useOutsideClick } from '@/hooks';
import { EducationalLevel, EmploymentStatus, FormData } from '@/types/form';
import { RefObject, useEffect, useState } from 'react';
import {
  FieldError,
  UseFormRegisterReturn,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import styles from './Select.module.scss';

interface Props {
  placeholder: string;
  label: string;
  id: string;
  values: EducationalLevel[] | EmploymentStatus[];
  currentValue: EducationalLevel | EmploymentStatus | null;
  error?: FieldError;
  register: UseFormRegisterReturn;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  mark: 'educational_level' | 'employment_status';
}

const Select = ({
  placeholder,
  label,
  id,
  values,
  error,
  register,
  setValue,
  trigger,
  mark,
  currentValue,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<
    EducationalLevel | EmploymentStatus | null
  >(currentValue);
  const [placeholderValue, setPlaceholderValue] = useState(placeholder);

  const sortRef = useOutsideClick(() => setIsOpen(false));

  useEffect(() => {
    if (selectedValue) {
      setValue?.(mark, selectedValue);
      trigger?.(mark);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  return (
    <div
      onClick={() => setIsOpen((prev) => !prev)}
      className={`${styles.container} ${error ? styles.errorField : ''}`}
    >
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <input id={id} type="text" className={styles.hiddenInput} {...register} />
      <div
        className={`${styles.wrapper} ${isOpen ? styles.toggledWrapper : ''}`}
      >
        <div className={styles.placeholder}>
          <span
            className={`${styles.placeholderValue} ${placeholderValue !== placeholder || selectedValue ? styles.visiblePlaceholder : ''}`}
          >
            {selectedValue || placeholderValue}
          </span>
          <span
            className={`${styles.iconWrapper} ${isOpen ? styles.openIcon : ''}`}
          >
            <ArrowDown />
          </span>
        </div>
        <ul
          ref={sortRef as RefObject<HTMLUListElement>}
          className={styles.optionsWrapper}
        >
          {values.map((value) => (
            <li
              onClick={() => setSelectedValue(value)}
              className={styles.option}
              key={value}
              onMouseEnter={() => setPlaceholderValue(value)}
              onMouseLeave={() => setPlaceholderValue(placeholder)}
            >
              {value}
            </li>
          ))}
        </ul>
      </div>
      {error && <p className={styles.error}>{error.message}</p>}
    </div>
  );
};

export default Select;
